import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LayoutTemplate, Plus, Trash2, Loader2, Eye, Code2, Sparkles, Copy, Palette, ExternalLink, Info } from "lucide-react";
import { Link } from "wouter";
import OverlayFieldEditor, { type OverlayField } from "./OverlayFieldEditor";
import { FileDown } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import {
  AUTO_VARIABLES,
  AUTO_VARIABLE_KEYS,
  DEFAULT_PROPOSAL_TEMPLATE_HTML,
  DEFAULT_PROPOSAL_TEMPLATE_VARIABLES,
  extractTemplateKeys,
  type ProposalVariableDef,
} from "@shared/proposals";

type TemplateRow = {
  id: number;
  name: string;
  description: string | null;
  engine: "html" | "canva";
  bodyHtml: string | null;
  variables: ProposalVariableDef[];
  validityDays: number;
  canvaMode: string;
  canvaTemplateId: string | null;
  canvaTemplateTitle: string | null;
  canvaFieldMap: Record<string, string> | null;
  overlayPdfMediaId: number | null;
  overlayFields: OverlayField[] | null;
};

const KIND_LABEL: Record<ProposalVariableDef["kind"], string> = {
  ai: "IA escreve",
  manual: "Vendedor digita",
  auto: "Automática",
};

/**
 * Ao reabrir um modelo overlay, os tamanhos de página vêm do servidor
 * (mesma função que o render usa) — o editor não deriva medidas sozinho.
 */
function OverlayPagesLoader({
  mediaId,
  pages,
  onPages,
  children,
}: {
  mediaId: number;
  pages: Array<{ width: number; height: number }>;
  onPages: (p: Array<{ width: number; height: number }>) => void;
  children: (pages: Array<{ width: number; height: number }>) => React.ReactNode;
}) {
  const q = trpc.crm.proposalTemplates.overlayPages.useQuery({ mediaId }, { enabled: pages.length === 0, retry: false });
  useEffect(() => { if (q.data && pages.length === 0) onPages(q.data.pages); }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps
  if (q.error) return <p className="text-xs text-red-700 dark:text-red-300">{q.error.message}</p>;
  if (!pages.length) return <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Carregando páginas…</div>;
  return <>{children(pages)}</>;
}

export default function ProposalTemplateSheet({
  open,
  onOpenChange,
  template,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** null = novo modelo */
  template: TemplateRow | null;
}) {
  const utils = trpc.useUtils();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [validityDays, setValidityDays] = useState(30);
  const [bodyHtml, setBodyHtml] = useState("");
  const [variables, setVariables] = useState<ProposalVariableDef[]>([]);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [tab, setTab] = useState<"html" | "preview">("html");
  const [engine, setEngine] = useState<"html" | "canva">("html");
  const [canvaMode, setCanvaMode] = useState<"placeholders" | "autofill" | "overlay">("overlay");
  const [overlayPdfMediaId, setOverlayPdfMediaId] = useState<number | null>(null);
  const [overlayPages, setOverlayPages] = useState<Array<{ width: number; height: number }>>([]);
  const [overlayFields, setOverlayFields] = useState<OverlayField[]>([]);
  const exportBase = trpc.crm.proposalTemplates.exportOverlayBase.useMutation({
    onSuccess: (r) => {
      setOverlayPdfMediaId(r.mediaId);
      setOverlayPages(r.pages);
      if (r.title) setCanvaTemplateTitle(r.title);
      toast.success(`PDF exportado do Canva — ${r.pages.length} página(s)`);
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Não consegui exportar o design")),
  });
  const [designInput, setDesignInput] = useState<string>("");
  const [designKeys, setDesignKeys] = useState<{ designId: string; title: string | null; pageCount: number; keys: string[] } | null>(null);
  const [canvaTemplateId, setCanvaTemplateId] = useState<string>("");
  const [canvaTemplateTitle, setCanvaTemplateTitle] = useState<string>("");
  const [canvaFieldMap, setCanvaFieldMap] = useState<Record<string, string>>({});

  const canvaStatus = trpc.canva.status.useQuery(undefined, { enabled: open });
  const canvaConnected = canvaStatus.data?.connected === true;
  const canvaTransport = canvaStatus.data?.connected ? canvaStatus.data.transport : null;
  const brandTemplates = trpc.canva.brandTemplates.list.useQuery(undefined, {
    enabled: open && engine === "canva" && canvaMode === "autofill" && canvaConnected,
    retry: false,
  });
  const recentDesigns = trpc.canva.designs.search.useQuery(undefined, {
    enabled: open && engine === "canva" && ((canvaMode === "placeholders" && canvaTransport === "mcp") || canvaMode === "overlay") && canvaConnected,
    retry: false,
  });
  const readKeys = trpc.useUtils().canva.designs.placeholders;
  const [readingKeys, setReadingKeys] = useState(false);
  const lerChaves = async (design: string) => {
    setReadingKeys(true);
    try {
      const r = await readKeys.fetch({ design });
      setDesignKeys(r);
      setCanvaTemplateId(r.designId);
      setCanvaTemplateTitle(r.title ?? r.designId);
      if (r.keys.length === 0) toast.warning("Nenhuma {{chave}} encontrada nesse design.");
    } catch (e) {
      toast.error(toUserFriendlyError(e as any, "Não consegui ler o design"));
    } finally {
      setReadingKeys(false);
    }
  };
  const dataset = trpc.canva.brandTemplates.dataset.useQuery(
    { brandTemplateId: canvaTemplateId },
    // Só no modo autofill: no modo placeholders o id é de um DESIGN, e o
    // dataset é coisa de brand template.
    { enabled: open && engine === "canva" && canvaMode === "autofill" && canvaConnected && !!canvaTemplateId, retry: false },
  );

  useEffect(() => {
    if (!open) return;
    setName(template?.name ?? "");
    setDescription(template?.description ?? "");
    setValidityDays(template?.validityDays ?? 30);
    setBodyHtml(template?.bodyHtml ?? DEFAULT_PROPOSAL_TEMPLATE_HTML);
    setVariables(template?.variables ?? DEFAULT_PROPOSAL_TEMPLATE_VARIABLES.map((v) => ({ ...v })));
    setPreviewHtml(null);
    setTab("html");
    setEngine(template?.engine ?? "html");
    setCanvaMode((template?.canvaMode as "placeholders" | "autofill" | "overlay") ?? "overlay");
    setDesignInput(template?.canvaMode === "placeholders" || template?.canvaMode === "overlay" ? template?.canvaTemplateId ?? "" : "");
    setOverlayPdfMediaId(template?.overlayPdfMediaId ?? null);
    setOverlayFields(template?.overlayFields ?? []);
    setOverlayPages([]);
    setDesignKeys(null);
    setCanvaTemplateId(template?.canvaTemplateId ?? "");
    setCanvaTemplateTitle(template?.canvaTemplateTitle ?? "");
    setCanvaFieldMap(template?.canvaFieldMap ?? {});
  }, [open, template?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Chaves usadas no HTML que não são automáticas nem declaradas — o
  // vendedor vai ter que digitar; melhor avisar aqui.
  const undeclared = useMemo(() => {
    const declared = new Set(variables.map((v) => v.key));
    return extractTemplateKeys(bodyHtml).filter((k) => !AUTO_VARIABLE_KEYS.has(k) && !k.startsWith("meta.") && !declared.has(k));
  }, [bodyHtml, variables]);

  const unused = useMemo(() => {
    const used = new Set(extractTemplateKeys(bodyHtml));
    return variables.filter((v) => !used.has(v.key)).map((v) => v.key);
  }, [bodyHtml, variables]);

  const onDone = () => {
    utils.crm.proposalTemplates.list.invalidate();
    onOpenChange(false);
  };
  const create = trpc.crm.proposalTemplates.create.useMutation({
    onSuccess: () => { toast.success("Modelo criado"); onDone(); },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao criar modelo")),
  });
  const update = trpc.crm.proposalTemplates.update.useMutation({
    onSuccess: () => { toast.success("Modelo salvo"); onDone(); },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao salvar modelo")),
  });
  const preview = trpc.crm.proposalTemplates.preview.useMutation({
    onSuccess: (r) => { setPreviewHtml(r.html); setTab("preview"); },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao gerar prévia")),
  });

  const saving = create.isPending || update.isPending;
  const mappedCount = Object.values(canvaFieldMap).filter(Boolean).length;
  const payload = {
    name: name.trim(),
    // null, não undefined: no update, undefined é "não mexer" e a descrição
    // apagada não seria salva.
    description: description.trim() || null,
    engine,
    bodyHtml: engine === "html" ? bodyHtml : null,
    variables,
    validityDays,
    canvaMode,
    canvaTemplateId: engine === "canva" ? canvaTemplateId || null : null,
    canvaTemplateTitle: engine === "canva" ? canvaTemplateTitle || null : null,
    canvaFieldMap: engine === "canva" && canvaMode === "autofill" ? Object.fromEntries(Object.entries(canvaFieldMap).filter(([, v]) => v)) : null,
    overlayPdfMediaId: engine === "canva" && canvaMode === "overlay" ? overlayPdfMediaId : null,
    overlayFields: engine === "canva" && canvaMode === "overlay" ? overlayFields : null,
  };
  const varsOk = variables.every((v) => /^[a-zA-Z0-9_]+$/.test(v.key) && v.label.trim());
  const canSave =
    name.trim().length > 0 && varsOk &&
    (engine === "html"
      ? bodyHtml.trim().length > 0
      : canvaMode === "placeholders" ? !!canvaTemplateId
      : canvaMode === "overlay" ? !!canvaTemplateId && !!overlayPdfMediaId && overlayFields.length > 0
      : !!canvaTemplateId && mappedCount > 0);

  // Classificação das chaves lidas do design: o sistema resolve, o modelo
  // declara, ou ninguém sabe (vai ficar como está no PDF).
  const keyStatus = (k: string): "auto" | "declared" | "unknown" =>
    AUTO_VARIABLE_KEYS.has(k) || k.startsWith("meta.") ? "auto" : variables.some((v) => v.key === k) ? "declared" : "unknown";

  // Opções para mapear um campo do brand template: automáticas + declaradas.
  const mapOptions = useMemo(() => {
    const declared = variables.filter((v) => v.key).map((v) => ({ key: v.key, label: `${v.label} (${KIND_LABEL[v.kind]})`, group: "Campos do modelo" }));
    const autos = AUTO_VARIABLES.filter((v) => v.key !== "assinatura_gestor").map((v) => ({ key: v.key, label: v.label, group: v.group }));
    return [...declared, ...autos];
  }, [variables]);

  const setVar = (i: number, patch: Partial<ProposalVariableDef>) =>
    setVariables((list) => list.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));

  const copyTag = (key: string) => {
    navigator.clipboard?.writeText(`{{${key}}}`).then(() => toast.success(`{{${key}}} copiado`)).catch(() => {});
  };

  const groups = useMemo(() => {
    const m = new Map<string, typeof AUTO_VARIABLES[number][]>();
    for (const v of AUTO_VARIABLES) m.set(v.group, [...(m.get(v.group) ?? []), v]);
    return [...m.entries()];
  }, []);

  return (
    <Sheet open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <SheetContent className="sm:max-w-[1100px] w-full p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <LayoutTemplate className="h-5 w-5" />
            </div>
            <div>
              <SheetTitle>{template ? "Editar modelo" : "Novo modelo de proposta"}</SheetTitle>
              <SheetDescription>HTML com variáveis <code className="text-xs">{"{{chave}}"}</code>. O PDF sai em A4.</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr_360px] overflow-hidden">
          {/* ── Editor / prévia ─────────────────────────────────────── */}
          <div className="flex flex-col min-h-0 border-b lg:border-b-0 lg:border-r">
            <div className="px-5 pt-4 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
              <div>
                <Label className="text-xs">Nome do modelo</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Proposta padrão — licitação" maxLength={200} />
              </div>
              <div>
                <Label className="text-xs">Validade (dias)</Label>
                <Input type="number" min={1} max={365} value={validityDays} onChange={(e) => setValidityDays(Math.max(1, Math.min(365, Number(e.target.value) || 30)))} className="w-28" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Descrição (aparece para o vendedor ao escolher o modelo)</Label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} />
              </div>
            </div>

            <div className="px-5 pt-3">
              <Label className="text-xs">Como a proposta é montada</Label>
              <div className="flex gap-2 mt-1">
                <Button type="button" size="sm" variant={engine === "html" ? "default" : "outline"} onClick={() => setEngine("html")}>
                  <Code2 className="h-3.5 w-3.5 mr-1.5" /> HTML (gerado pelo sistema)
                </Button>
                <Button type="button" size="sm" variant={engine === "canva" ? "default" : "outline"} onClick={() => setEngine("canva")}>
                  <Palette className="h-3.5 w-3.5 mr-1.5" /> Brand template do Canva
                </Button>
              </div>
            </div>

            {engine === "canva" && (
              <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-3 pb-4 space-y-4">
                {!canvaConnected ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3 text-sm text-amber-900 dark:text-amber-200">
                    O Canva não está conectado.{" "}
                    <Link href="/settings?integration=canva" className="underline font-medium inline-flex items-center gap-0.5">Conectar em Integrações <ExternalLink className="h-3 w-3" /></Link>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs">Tipo de modelo no Canva</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        <Button type="button" size="sm" variant={canvaMode === "overlay" ? "default" : "outline"} onClick={() => setCanvaMode("overlay")}>
                          PDF do Canva + campos por cima
                        </Button>
                        <Button type="button" size="sm" variant={canvaMode === "placeholders" ? "default" : "outline"} onClick={() => setCanvaMode("placeholders")}>
                          Design com {"{{chaves}}"}
                        </Button>
                        <Button type="button" size="sm" variant={canvaMode === "autofill" ? "default" : "outline"} onClick={() => setCanvaMode("autofill")}>
                          Brand template + Autofill
                        </Button>
                      </div>
                    </div>

                    {canvaMode === "overlay" && (
                      <div className="space-y-3">
                        <p className="text-xs text-muted-foreground">
                          Funciona em qualquer plano e conexão do Canva: o design é exportado em PDF e vira o fundo; você marca onde entra cada dado e o sistema escreve por cima ao gerar.
                          No Canva, deixe as áreas variáveis com texto de exemplo (as caixas cobrem) ou em branco.
                        </p>
                        <div>
                          <Label className="text-xs">Design no Canva (link ou id)</Label>
                          <div className="flex gap-2 mt-1">
                            <Input value={designInput} onChange={(e) => setDesignInput(e.target.value)} placeholder="https://www.canva.com/design/DAF…/edit ou DAF…" />
                            <Button type="button" size="sm" variant="outline" disabled={!designInput.trim() || exportBase.isPending} onClick={() => {
                              const m = designInput.trim().match(/(D[a-zA-Z0-9_-]{6,})/);
                              if (!m) { toast.error("Informe o id do design (começa com D) ou o link dele."); return; }
                              setCanvaTemplateId(m[1]);
                              exportBase.mutate({ designId: m[1], replaceMediaId: overlayPdfMediaId });
                            }}>
                              {exportBase.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><FileDown className="h-3.5 w-3.5 mr-1" /> {overlayPdfMediaId ? "Reexportar PDF" : "Exportar PDF do Canva"}</>}
                            </Button>
                          </div>
                        </div>
                        {recentDesigns.data && recentDesigns.data.length > 0 && !overlayPdfMediaId && (
                          <div>
                            <Label className="text-[10px]">Ou escolha um design recente</Label>
                            <div className="mt-1 max-h-40 overflow-y-auto rounded-md border divide-y">
                              {recentDesigns.data.map((d) => (
                                <button key={d.id} type="button" className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent" onClick={() => { setDesignInput(d.id); setCanvaTemplateId(d.id); setCanvaTemplateTitle(d.title); exportBase.mutate({ designId: d.id }); }}>
                                  <span className="font-medium">{d.title}</span>
                                  <span className="text-muted-foreground"> · {d.pageCount ?? "?"} pág. · <span className="font-mono">{d.id}</span></span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {overlayPdfMediaId && (
                          <OverlayPagesLoader mediaId={overlayPdfMediaId} pages={overlayPages} onPages={setOverlayPages}>
                            {(pages) => (
                              <OverlayFieldEditor
                                pdfUrl={`/api/crm/proposal-templates/base-pdf/${overlayPdfMediaId}`}
                                pages={pages}
                                fields={overlayFields}
                                onChange={setOverlayFields}
                                keyOptions={[
                                  ...variables.filter((v) => v.key).map((v) => ({ key: v.key, label: `${v.label} (${KIND_LABEL[v.kind]})`, group: "Campos do modelo" })),
                                  ...AUTO_VARIABLES.filter((v) => !v.html).map((v) => ({ key: v.key, label: v.label, group: v.group })),
                                ]}
                              />
                            )}
                          </OverlayPagesLoader>
                        )}
                      </div>
                    )}

                    {canvaMode === "placeholders" && (
                      <div className="space-y-3">
                        {canvaTransport !== "mcp" ? (
                          <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3 text-xs text-amber-900 dark:text-amber-200 flex gap-2">
                            <Info className="h-4 w-4 shrink-0 mt-0.5" />
                            <span>Trocar textos num design exige a conexão do Canva por <strong>MCP</strong>. Em Integrações → Canva, use “Migrar para MCP”.</span>
                          </div>
                        ) : (
                          <>
                            <p className="text-xs text-muted-foreground">
                              No Canva, monte a proposta com textos como <code>{"{{cliente_organizacao}}"}</code>, <code>{"{{negociacao_valor}}"}</code>, <code>{"{{apresentacao}}"}</code>.
                              Cada proposta gerada é uma cópia desse design com as chaves trocadas — o original nunca é alterado.
                            </p>
                            <div>
                              <Label className="text-xs">Design-modelo (link ou id)</Label>
                              <div className="flex gap-2 mt-1">
                                <Input value={designInput} onChange={(e) => setDesignInput(e.target.value)} placeholder="https://www.canva.com/design/DAF…/edit ou DAF…" />
                                <Button type="button" size="sm" variant="outline" disabled={!designInput.trim() || readingKeys} onClick={() => lerChaves(designInput)}>
                                  {readingKeys ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Ler chaves"}
                                </Button>
                              </div>
                            </div>
                            {recentDesigns.data && recentDesigns.data.length > 0 && (
                              <div>
                                <Label className="text-[10px]">Ou escolha um design recente</Label>
                                <div className="mt-1 max-h-40 overflow-y-auto rounded-md border divide-y">
                                  {recentDesigns.data.map((d) => (
                                    <button key={d.id} type="button" className={`w-full text-left px-3 py-1.5 text-xs hover:bg-accent ${canvaTemplateId === d.id ? "bg-accent" : ""}`} onClick={() => { setDesignInput(d.id); lerChaves(d.id); }}>
                                      <span className="font-medium">{d.title}</span>
                                      <span className="text-muted-foreground"> · {d.pageCount ?? "?"} pág. · <span className="font-mono">{d.id}</span></span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            {canvaTemplateId && (
                              <div className="rounded-md border p-3 space-y-2">
                                <div className="text-xs"><span className="text-muted-foreground">Design:</span> <strong>{canvaTemplateTitle || canvaTemplateId}</strong> <span className="font-mono text-muted-foreground">({canvaTemplateId})</span>{designKeys ? ` · ${designKeys.pageCount} página(s)` : ""}</div>
                                {designKeys && (
                                  <div className="space-y-1">
                                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Chaves encontradas</div>
                                    <div className="flex flex-wrap gap-1">
                                      {designKeys.keys.map((k) => {
                                        const st = keyStatus(k);
                                        return (
                                          <Badge key={k} variant="outline" className={`font-mono text-[10px] ${st === "auto" ? "border-emerald-300 text-emerald-800 dark:text-emerald-300" : st === "declared" ? "border-blue-300 text-blue-800 dark:text-blue-300" : "border-amber-300 text-amber-800 dark:text-amber-300 cursor-pointer"}`}
                                            title={st === "auto" ? "Preenchida pelo sistema" : st === "declared" ? "Campo do modelo (IA ou vendedor)" : "Sem campo — clique para criar como campo manual"}
                                            onClick={() => st === "unknown" && setVariables((l) => [...l, { key: k, label: k, kind: "manual", multiline: true }])}
                                          >
                                            {k}
                                          </Badge>
                                        );
                                      })}
                                    </div>
                                    <p className="text-[10px] text-muted-foreground">Verde: automática · Azul: campo do modelo · Âmbar: sem campo (clique para criar).</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {canvaMode === "autofill" && canvaStatus.data?.connected && canvaStatus.data.hasAutofill === false && (
                      <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3 text-xs text-amber-900 dark:text-amber-200 flex gap-2">
                        <Info className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>A conta do Canva conectada <strong>não tem Autofill</strong> (recurso do Canva Enterprise). Prefira “Design com chaves”.</span>
                      </div>
                    )}
                    {canvaMode === "autofill" && (
                    <div>
                      <Label className="text-xs">Brand template</Label>

                      {brandTemplates.isLoading ? (
                        <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1"><Loader2 className="h-4 w-4 animate-spin" /> Carregando templates da conta…</div>
                      ) : brandTemplates.error ? (
                        <p className="text-sm text-red-700 dark:text-red-300 mt-1">{brandTemplates.error.message}</p>
                      ) : (brandTemplates.data?.length ?? 0) === 0 ? (
                        <p className="text-sm text-muted-foreground mt-1">Nenhum brand template na conta. Crie um no Canva (Marca → Modelos) com campos de dados e volte aqui.</p>
                      ) : (
                        <Select
                          value={canvaTemplateId}
                          onValueChange={(id) => {
                            setCanvaTemplateId(id);
                            setCanvaTemplateTitle(brandTemplates.data?.find((t) => t.id === id)?.title ?? "");
                            setCanvaFieldMap({});
                          }}
                        >
                          <SelectTrigger><SelectValue placeholder="Escolha o brand template" /></SelectTrigger>
                          <SelectContent>
                            {brandTemplates.data!.map((t) => (
                              <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    )}

                    {canvaMode === "autofill" && canvaTemplateId && (
                      <div>
                        <Label className="text-xs">Campos do template → o que entra em cada um</Label>
                        {dataset.isLoading ? (
                          <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1"><Loader2 className="h-4 w-4 animate-spin" /> Lendo campos…</div>
                        ) : dataset.error ? (
                          <p className="text-sm text-red-700 dark:text-red-300 mt-1">{dataset.error.message}</p>
                        ) : (dataset.data?.fields.length ?? 0) === 0 ? (
                          <p className="text-sm text-muted-foreground mt-1">
                            Este template não tem campos de dados. No Canva, marque os textos como campos de dados (Autofill) para o sistema preenchê-los.
                          </p>
                        ) : (
                          <div className="space-y-2 mt-2">
                            {dataset.data!.fields.map((f) => (
                              <div key={f.name} className="grid grid-cols-[1fr_1.4fr] gap-2 items-center">
                                <div className="text-sm truncate">
                                  <span className="font-mono text-xs">{f.name}</span>
                                  <Badge variant="outline" className="ml-1.5 text-[10px]">{f.type}</Badge>
                                </div>
                                <Select value={canvaFieldMap[f.name] ?? ""} onValueChange={(v) => setCanvaFieldMap((m) => ({ ...m, [f.name]: v === "__none__" ? "" : v }))}>
                                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="— não preencher —" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none__">— não preencher —</SelectItem>
                                    {f.type === "image"
                                      ? <SelectItem value="empresa_logo">Logo da empresa</SelectItem>
                                      : mapOptions.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <Tabs value={tab} onValueChange={(v) => setTab(v as any)} className={`flex-1 min-h-0 flex-col px-5 pt-3 pb-4 ${engine === "html" ? "flex" : "hidden"}`}>
              <div className="flex items-center justify-between gap-2">
                <TabsList>
                  <TabsTrigger value="html"><Code2 className="h-3.5 w-3.5 mr-1.5" /> HTML</TabsTrigger>
                  <TabsTrigger value="preview" onClick={() => !previewHtml && preview.mutate({ bodyHtml, variables })}><Eye className="h-3.5 w-3.5 mr-1.5" /> Prévia</TabsTrigger>
                </TabsList>
                <Button size="sm" variant="outline" disabled={preview.isPending} onClick={() => preview.mutate({ bodyHtml, variables })}>
                  {preview.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Eye className="h-3.5 w-3.5 mr-1.5" />} Atualizar prévia
                </Button>
              </div>
              <TabsContent value="html" className="flex-1 min-h-0 mt-3">
                <Textarea
                  value={bodyHtml}
                  onChange={(e) => { setBodyHtml(e.target.value); setPreviewHtml(null); }}
                  spellCheck={false}
                  className="h-full min-h-[320px] font-mono text-xs leading-relaxed resize-none"
                />
              </TabsContent>
              <TabsContent value="preview" className="flex-1 min-h-0 mt-3">
                <div className="h-full min-h-[320px] rounded-md border bg-white overflow-hidden">
                  {previewHtml ? (
                    <iframe srcDoc={previewHtml} title="Prévia do modelo" className="w-full h-full" sandbox="" />
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                      {preview.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Clique em “Atualizar prévia”."}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* ── Variáveis ───────────────────────────────────────────── */}
          <div className="overflow-y-auto px-5 py-4 space-y-5">
            <div>
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold">Campos do modelo</h4>
                <Button size="sm" variant="outline" onClick={() => setVariables((l) => [...l, { key: `campo_${l.length + 1}`, label: "Novo campo", kind: "manual", multiline: true }])}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Campo
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Textos que a IA escreve ou o vendedor digita. Use no HTML como <code>{"{{chave}}"}</code>.</p>
              <div className="space-y-3 mt-3">
                {variables.map((v, i) => (
                  <div key={i} className="rounded-md border p-3 space-y-2">
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                      <div>
                        <Label className="text-[10px]">Chave</Label>
                        <Input value={v.key} onChange={(e) => setVar(i, { key: e.target.value.replace(/[^a-zA-Z0-9_]/g, "_") })} className="h-8 text-xs font-mono" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Rótulo</Label>
                        <Input value={v.label} onChange={(e) => setVar(i, { label: e.target.value })} className="h-8 text-xs" />
                      </div>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={() => setVariables((l) => l.filter((_, idx) => idx !== i))}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
                      <Select value={v.kind} onValueChange={(k) => setVar(i, { kind: k as ProposalVariableDef["kind"] })}>
                        <SelectTrigger className="h-8 text-xs w-[150px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ai"><span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> {KIND_LABEL.ai}</span></SelectItem>
                          <SelectItem value="manual">{KIND_LABEL.manual}</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="ghost" className="h-8 justify-start text-xs font-mono" onClick={() => copyTag(v.key)}>
                        <Copy className="h-3 w-3 mr-1" /> {`{{${v.key}}}`}
                      </Button>
                    </div>
                    {v.kind === "ai" ? (
                      <div>
                        <Label className="text-[10px]">Instrução para a IA</Label>
                        <Textarea rows={3} value={v.prompt ?? ""} onChange={(e) => setVar(i, { prompt: e.target.value })} className="text-xs" placeholder="O que a IA deve escrever neste campo, com base no histórico da negociação." />
                      </div>
                    ) : (
                      <div>
                        <Label className="text-[10px]">Valor padrão (opcional)</Label>
                        <Textarea rows={2} value={v.defaultValue ?? ""} onChange={(e) => setVar(i, { defaultValue: e.target.value })} className="text-xs" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {undeclared.length > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                  No HTML, mas sem campo declarado (o vendedor vai digitar): {undeclared.map((k) => <code key={k} className="mr-1">{`{{${k}}}`}</code>)}
                </p>
              )}
              {unused.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Declarados mas não usados no HTML: {unused.join(", ")}
                </p>
              )}
            </div>

            <Separator />

            <div>
              <h4 className="text-sm font-semibold">Variáveis automáticas</h4>
              <p className="text-xs text-muted-foreground mt-1">Preenchidas pelo sistema. Clique para copiar. Metadados da negociação: <code>{"{{meta.campo}}"}</code>.</p>
              <div className="space-y-3 mt-3">
                {groups.map(([group, list]) => (
                  <div key={group}>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">{group}</div>
                    <div className="flex flex-wrap gap-1">
                      {list.map((v) => (
                        <Badge key={v.key} variant="outline" className="cursor-pointer font-mono text-[10px] hover:bg-accent" title={v.label} onClick={() => copyTag(v.key)}>
                          {v.key}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <SheetFooter className="px-6 py-4 border-t flex-row justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button
            disabled={!canSave || saving}
            onClick={() => (template ? update.mutate({ id: template.id, ...payload }) : create.mutate(payload))}
          >
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {template ? "Salvar modelo" : "Criar modelo"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
